import { parse } from 'regexparam';
import { loc } from './Router.svelte';
import type { LocationInfo } from './Router.svelte';

/** Options for the `active` action */
export interface ActiveOptions {
	/** Path to match; if empty, will default to the link's target */
	path?: string | RegExp;
	/** Name of the CSS class to add when the route is active; default is "active" */
	className?: string;
	/** Name of the CSS class to add when the route is inactive; nothing added by default */
	inactiveClassName?: string;
}

/** Internal representation of a tracked node */
interface ActiveNode {
	/** The DOM element being tracked */
	node: HTMLElement;
	/** CSS class(es) to add when active */
	className: string;
	/** CSS class(es) to add when inactive */
	inactiveClassName?: string;
	/** Compiled regex pattern for matching */
	pattern: RegExp;
}

/** List of nodes to update */
const nodes: ActiveNode[] = [];

/** Current location */
let location: string;

/** Function that updates a node marking it active or inactive */
function checkActive(el: ActiveNode): void {
	const matchesLocation = el.pattern.test(location);
	toggleClasses(el, el.className, matchesLocation);
	toggleClasses(el, el.inactiveClassName, !matchesLocation);
}

/** Toggle CSS classes on/off for a node */
function toggleClasses(el: ActiveNode, className: string | undefined, shouldAdd: boolean): void {
	(className || '').split(' ').forEach((cls: string) => {
		if (!cls) {
			return;
		}
		// Remove the class first
		el.node.classList.remove(cls);

		// If the pattern matches, then set the class
		if (shouldAdd) {
			el.node.classList.add(cls);
		}
	});
}

// Listen to changes in the location
loc.subscribe((value: LocationInfo) => {
	// Update the location
	location = value.location + (value.querystring ? '?' + value.querystring : '');

	// Update all nodes
	nodes.map(checkActive);
});

/**
 * Svelte Action for automatically adding the "active" class to elements (links, or any other DOM element) when the current location matches a certain path.
 *
 * @param node - The target node (automatically set by Svelte)
 * @param opts - Can be an object of type `ActiveOptions`, or a string (or regular expressions) representing `ActiveOptions.path`.
 * @returns Destroy function
 */
export default function active(node: HTMLElement, opts?: ActiveOptions | string | RegExp): { destroy: () => void } {
	let resolvedOpts: ActiveOptions;
	// Normalize opts: interpret strings and RegExps as the path option
	if (opts && (typeof opts == 'string' || (typeof opts == 'object' && opts instanceof RegExp))) {
		// Interpret strings and regular expressions as opts.path
		resolvedOpts = {
			path: opts
		};
	} else {
		// Ensure opts is a dictionary
		resolvedOpts = (opts as ActiveOptions) || {};
	}

	// Path defaults to link target
	if (!resolvedOpts.path && node.hasAttribute('href')) {
		resolvedOpts.path = node.getAttribute('href') || undefined;
		if (resolvedOpts.path && resolvedOpts.path.length > 1 && (resolvedOpts.path as string).charAt(0) == '#') {
			resolvedOpts.path = (resolvedOpts.path as string).substring(1);
		}
	}

	// Default class name
	if (!resolvedOpts.className) {
		resolvedOpts.className = 'active';
	}

	// Validate that the path starts with '/' or '*' (required format for route matching)
	if (
		!resolvedOpts.path ||
		/* v8 ignore next -- length<1 is unreachable: empty string is already falsy */
		(typeof resolvedOpts.path == 'string' &&
			(resolvedOpts.path.length < 1 ||
				(resolvedOpts.path.charAt(0) != '/' && resolvedOpts.path.charAt(0) != '*')))
	) {
		throw Error('Invalid value for "path" argument');
	}

	// If path is not a regular expression already, make it
	const { pattern } =
		typeof resolvedOpts.path == 'string' ? parse(resolvedOpts.path) : { pattern: resolvedOpts.path };

	// Add the node to the list
	const el: ActiveNode = {
		node,
		className: resolvedOpts.className,
		inactiveClassName: resolvedOpts.inactiveClassName,
		pattern
	};
	nodes.push(el);

	// Trigger the action right away
	checkActive(el);

	return {
		// When the element is destroyed, remove it from the list
		destroy(): void {
			nodes.splice(nodes.indexOf(el), 1);
		}
	};
}
